import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Stack } from "@mui/material";
import { FormCategory } from "@simplyk/common";
import * as QrCode from "qrcode";

import { CloseDialogButton } from "./CloseDialogButton";
import { ShareFormTabLayout } from "./ShareFormTabLayout";
import { ShareFormTabTitle } from "./ShareFormTabTitle";

import { ButtonCopy } from "@/components/ButtonCopy/ButtonCopy";
import { AmplitudeEvents } from "@/constants/amplitude";
import { DashboardButton } from "@/dashboard-design-system/Button/DashboardButton";
import { DashboardInfobox } from "@/dashboard-design-system/Infobox";
import { useAmplitude } from "@/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/hooks/useTranslate";
import { ChevronLeft } from "@/icons/outlined";

type QrCodeTabProps = {
  formLink: string;
  formCategory: FormCategory;
  onClose?: () => void;
  onBackClick?: () => void;
};

export const QrCodeTab: FC<QrCodeTabProps> = ({ formLink, formCategory, onClose, onBackClick }) => {
  const { t } = useTranslate();
  const { logAmplitudeEvent } = useAmplitude();
  const [image, setImage] = useState<{ url: string; blob: Blob } | null>(null);

  const qrCodeCanvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (qrCodeCanvasRef?.current && formLink) {
      QrCode.toCanvas(qrCodeCanvasRef.current, formLink);
      qrCodeCanvasRef.current.toBlob((blob) => {
        blob && setImage({ url: URL.createObjectURL(blob), blob });
      });
    }
  }, [qrCodeCanvasRef, formLink]);

  const clipboardItem = useMemo(() => {
    if (image?.blob) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      return new ClipboardItem({ "image/png": image?.blob });
    }
    return null;
  }, [image?.blob]);

  const handleOnDownload = useCallback(() => {
    logAmplitudeEvent(AmplitudeEvents.ShareFormModalClickSharingQrCode, {
      form_type: formCategory,
      qr_code_sharing_type: "Download image",
    });

    if (image?.url) {
      const a = document.createElement("a");
      a.href = image.url;
      a.download = "qr-code.png";
      a.click();
    }
  }, [formCategory, image?.url, logAmplitudeEvent]);

  return (
    <ShareFormTabLayout
      slots={{
        closeButton: <CloseDialogButton onClick={onClose} />,
        header: <ShareFormTabTitle>{t("dashboard", "shareFormModal.qrCode.title")}</ShareFormTabTitle>,
        content: (
          <>
            <DashboardInfobox
              vibe="brand"
              type="alert"
              size="medium"
              pointer="bottom"
              title={t("dashboard", "shareFormModal.qrCode.infoBox.title")}
              hideIcon
            >
              {t("dashboard", "shareFormModal.qrCode.infoBox.content")}
            </DashboardInfobox>
            <Stack alignItems="center">
              <canvas ref={qrCodeCanvasRef} width="148px" height="148px" />
              <Stack direction="row" alignItems="center" gap={1}>
                <ButtonCopy
                  loading={false}
                  vibe="brand"
                  size="large"
                  variant="outlined"
                  toCopy={clipboardItem ? [clipboardItem] : null}
                  copyText={t("dashboard", "common.copyImage")}
                  amplitudeEvent={AmplitudeEvents.ShareFormModalClickSharingQrCode}
                  amplitudePayload={{ form_type: formCategory, qr_code_sharing_type: "Copy image" }}
                />
                <DashboardButton size="large" vibe="brand" variant="filled" onClick={handleOnDownload}>
                  {t("dashboard", "common.downloadImage")}
                </DashboardButton>
              </Stack>
            </Stack>
          </>
        ),
        footer: (
          <Stack direction="row" alignItems="center" justifyContent="flex-start">
            <DashboardButton
              vibe="neutral"
              variant="outlined"
              size="large"
              startIcon={<ChevronLeft />}
              onClick={onBackClick}
            >
              {t("common", "back")}
            </DashboardButton>
          </Stack>
        ),
      }}
    />
  );
};
